"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Instagram, Linkedin, Facebook } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const SocialIcons = ({
  className
}: {
  className?: string;
}) => <div className={cn("flex items-center gap-4", className)}>
    <Link to="https://instagram.com" newTab className="text-[#013183] transition-transform duration-300 hover:scale-110" aria-label="Instagram">
      <Instagram className="h-5 w-5" />
    </Link>
    <Link to="https://pinterest.com" newTab className="text-[#013183] transition-transform duration-300 hover:scale-110" aria-label="Pinterest">
      {}
      <Facebook className="h-5 w-5" />
    </Link>
    <Link to="https://linkedin.com" newTab className="text-[#013183] transition-transform duration-300 hover:scale-110" aria-label="LinkedIn">
      <Linkedin className="h-5 w-5" />
    </Link>
  </div>;
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-16 animate-pulse rounded bg-gray-200" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative block text-sm font-medium uppercase tracking-[0.1em] text-[#013183] transition-colors hover:text-[#013183]/80 focus:outline-none">
                {item.label}
                <span className="absolute -bottom-2 left-1/2 h-1.5 w-1.5 -translate-x-1/2 scale-0 rounded-full bg-[#f97a00] opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col items-center gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-32 animate-pulse rounded bg-gray-200" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center gap-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-medium uppercase tracking-widest text-[#013183] transition-colors hover:text-[#f97a00]" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="main-navigation" className="dark bg-background text-foreground">
      <header className={cn("fixed left-0 right-0 top-0 z-50 w-full transition-all duration-300 ease-in-out", isScrolled ? "border-b border-[#e5e5e5] bg-white/80 py-3 shadow-sm backdrop-blur-md" : "bg-white py-6")}>
        <div className="container mx-auto flex items-center justify-between px-4 md:px-8">
          {}
          <div className="flex-shrink-0">
            <Link to="/HomePage#" className="block">
              <img src="https://humble-drake.10web.cloud/wp-content/uploads/2026/02/cropped-SayKen-Tours-Final-Logo-1-e1728386750833.png" alt="Unforgettable Memories of Israel, One Tour at a Time" className="object-contain !w-[100px] !h-[60px]" />
            </Link>
          </div>

          {}
          <div className="hidden md:block">
            <MenuProvider menu_id="17" className="flex justify-center">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4">
            <div className="hidden md:block">
              <SocialIcons />
            </div>

            {}
            <Button variant="ghost" size="icon" className="text-[#013183] md:hidden" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </header>

      {}
      <div className={cn("fixed inset-0 z-[60] flex flex-col bg-white transition-transform duration-300 md:hidden", isMobileMenuOpen ? "translate-x-0" : "translate-x-full")}>
        <div className="flex items-center justify-between px-4 py-6">
          <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
            <img src="/img/unforgettable_israel_memories_logo.png" alt="Unforgettable Memories of Israel, One Tour at a Time" className="h-7 w-auto object-contain" />
          </Link>
          <Button variant="ghost" size="icon" className="text-[#013183]" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
            <X className="h-6 w-6" />
          </Button>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center gap-12 p-8">
          <MenuProvider menu_id="17" className="w-full">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>

          <div className="mt-8">
            <SocialIcons className="gap-8" />
          </div>
        </div>
      </div>
    </section>;
}